import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import BlockchainStatusIndicator from '../../components/ui/BlockchainStatusIndicator';
import CriticalAlertsPanel from '../../components/ui/CriticalAlertsPanel';
import QuickActionLauncher from '../../components/ui/QuickActionLauncher';

// Import page-specific components
import BatchInfoForm from './components/BatchInfoForm';
import TestParametersForm from './components/TestParametersForm';
import BlockchainLogger from './components/BlockchainLogger';
import BatchHistoryPanel from './components/BatchHistoryPanel';
import DigitalSignatureCapture from './components/DigitalSignatureCapture';

const BatchTestingRecords = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [batchData, setBatchData] = useState(null);
  const [testData, setTestData] = useState(null);
  const [blockchainTransaction, setBlockchainTransaction] = useState(null);
  const [signatures, setSignatures] = useState({});
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState('draft'); // draft, testing, review, completed

  const steps = [
    { id: 1, name: 'Batch Information', icon: 'Package', description: 'Enter batch details and specifications' },
    { id: 2, name: 'Test Parameters', icon: 'FlaskConical', description: 'Configure and execute quality tests' },
    { id: 3, name: 'Digital Signatures', icon: 'PenTool', description: 'Capture required approvals' },
    { id: 4, name: 'Blockchain Logging', icon: 'Blocks', description: 'Store immutable records' }
  ];

  useEffect(() => {
    // Set page title
    document.title = 'Batch Testing Records - IFET ChainLog';
  }, []);

  const handleBatchSubmit = async (formData) => {
    try {
      setBatchData(formData);
      setWorkflowStatus('testing');
      setCurrentStep(2);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Batch data saved:', formData);
    } catch (error) {
      console.error('Error saving batch data:', error);
    }
  };

  const handleTestSubmit = async (testResults) => {
    try {
      setTestData(testResults);
      setWorkflowStatus('review');
      setCurrentStep(3);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Test data saved:', testResults);
    } catch (error) {
      console.error('Error saving test data:', error);
    }
  };

  const handleSignatureCapture = (capturedSignatures) => {
    setSignatures(capturedSignatures);
    setCurrentStep(4);
    console.log('Signatures captured:', capturedSignatures);
  };

  const handleBlockchainComplete = (transactionData) => {
    setBlockchainTransaction(transactionData);
    setWorkflowStatus('completed');
    console.log('Blockchain transaction completed:', transactionData);
  };

  const handleBatchSelect = (batch) => {
    setSelectedBatch(batch);
    // Optionally load batch data for editing
    if (batch?.status === 'pending') {
      setBatchData({
        batchNumber: batch?.batchNumber,
        productName: batch?.productName,
        productType: batch?.productType,
        // ... other fields
      });
      setCurrentStep(2);
    }
  };

  const handleStepNavigation = (stepId) => {
    // Only allow navigation to completed steps or next step
    if (stepId <= currentStep || (stepId === currentStep + 1 && canProceedToStep(stepId))) {
      setCurrentStep(stepId);
    }
  };

  const canProceedToStep = (stepId) => {
    switch (stepId) {
      case 2:
        return batchData !== null;
      case 3:
        return batchData !== null && testData !== null;
      case 4:
        return batchData !== null && testData !== null && Object.keys(signatures)?.length > 0;
      default:
        return true;
    }
  };

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'active';
    if (canProceedToStep(stepId)) return 'available';
    return 'disabled';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BatchInfoForm
            onSubmit={handleBatchSubmit}
            initialData={batchData}
            isEditing={batchData !== null}
          />
        );
      case 2:
        return (
          <TestParametersForm
            batchData={batchData}
            onSubmit={handleTestSubmit}
            onSave={(data) => console.log('Test progress saved:', data)}
          />
        );
      case 3:
        return (
          <DigitalSignatureCapture
            onSignatureCapture={handleSignatureCapture}
            requiredSignatures={['technician', 'supervisor']}
            isVisible={true}
          />
        );
      case 4:
        return (
          <BlockchainLogger
            batchData={batchData}
            testData={testData}
            onTransactionComplete={handleBlockchainComplete}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/quality-dashboard')}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors duration-150"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Batch Testing Records</h1>
                <p className="text-muted-foreground mt-1">
                  Pharmaceutical quality assurance with blockchain verification
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <BlockchainStatusIndicator />
              <Button
                variant="outline"
                onClick={() => setIsAlertsOpen(true)}
                iconName="Bell"
                iconPosition="left"
              >
                Alerts
              </Button>
            </div>
          </div>

          {/* Workflow Status */}
          <div className="bg-card border border-border rounded-lg p-4 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  workflowStatus === 'draft' ? 'bg-muted-foreground' :
                  workflowStatus === 'testing' ? 'bg-warning' :
                  workflowStatus === 'review'? 'bg-primary' : 'bg-success'
                }`} />
                <span className="font-medium text-foreground capitalize">
                  {workflowStatus} Status
                </span>
                {batchData && (
                  <span className="text-sm text-muted-foreground">
                    • Batch: {batchData?.batchNumber}
                  </span>
                )}
              </div>
              
              <div className="text-sm text-muted-foreground">
                Step {currentStep} of {steps?.length}
              </div>
            </div>
          </div>

          {/* Step Navigation */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between">
              {steps?.map((step, index) => {
                const status = getStepStatus(step?.id);
                const isLast = index === steps?.length - 1;
                
                return (
                  <React.Fragment key={step?.id}>
                    <button
                      onClick={() => handleStepNavigation(step?.id)}
                      disabled={status === 'disabled'}
                      className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-150 ${
                        status === 'active' ? 'bg-primary text-primary-foreground' :
                        status === 'completed' ? 'bg-success/10 text-success hover:bg-success/20' :
                        status === 'available' ? 'hover:bg-muted text-foreground' :
                        'text-muted-foreground cursor-not-allowed opacity-50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        status === 'active' ? 'bg-primary-foreground/20' :
                        status === 'completed'? 'bg-success/20' : 'bg-muted'
                      }`}>
                        <Icon 
                          name={status === 'completed' ? 'Check' : step?.icon} 
                          size={16}
                          className={
                            status === 'active' ? 'text-primary-foreground' :
                            status === 'completed'? 'text-success' : 'text-muted-foreground'
                          }
                        />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{step?.name}</p>
                        <p className="text-xs opacity-80">{step?.description}</p>
                      </div>
                    </button>
                    {!isLast && (
                      <div className={`flex-1 h-px mx-4 ${
                        step?.id < currentStep ? 'bg-success' : 'bg-border'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Primary Content */}
            <div className="xl:col-span-3">
              {renderStepContent()}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <BatchHistoryPanel
                currentBatch={batchData}
                onBatchSelect={handleBatchSelect}
              />
            </div>
          </div>
        </div>
      </main>
      {/* Critical Alerts Panel */}
      <CriticalAlertsPanel
        isOpen={isAlertsOpen}
        onClose={() => setIsAlertsOpen(false)}
      />
      {/* Quick Action Launcher */}
      <QuickActionLauncher />
    </div>
  );
};

export default BatchTestingRecords;